// ============================================================================
// REGOLE DI OCCUPAZIONE AUTISTA — fonte di verità unica.
//
// Contratto: docs/architecture/driver-occupancy.md (regole OCC-1..OCC-9).
// Questo modulo è copiato verbatim nel repo Functions da
// `functions/scripts/syncOccupancyRules.js`: **non deve avere alcun import**
// (niente dayjs, @utils/dates, @/constants) e deve lavorare solo su numeri e
// oggetti semplici. L'adattamento delle forme dati — doc Firestore lato
// dispatch, riga CockroachDB lato analytics — è responsabilità del chiamante.
//
// Le regole replicano `dataHandlersByType.order` in
// `src/modules/logic/timelineDataHandlers.js` e le inclusioni di
// `buildUtilizationMap` in `src/features/home/Timeline/logic/utilizationHelpers.js`.
// Una fixture golden condivisa (`__fixtures__/occupancy-golden.json`) è
// asserita in entrambi i repo: non cambiare una regola qui senza rigenerarla.
// ============================================================================

/** OCC-1 — soglia del doppio formato storico delle durate: > 180 ⇒ secondi. */
export const SECONDS_THRESHOLD = 180;

/** OCC-1 — durata di default quando `tempo` manca o è zero. */
export const DEFAULT_DURATION_MIN = 45;

/**
 * OCC-5 — un'assistenza del secondo operatore (una salita o una discesa).
 * Stesso valore di `OP2_ASSIST_MIN` in `secondOperatorSchedule.js`.
 */
export const SECOND_OPERATOR_MIN = 10;

/** OCC-7 — stati che marcano una corsa come conclusa (5 = listener, 10 = app). */
export const COMPLETED_ORDER_STATUSES = [5, 10];

/** OCC-8 — stati di annullamento (6 = annullato, 19 = annullato 24h, 101 = rifiutato). */
export const CANCELLED_ORDER_STATUSES = [6, 19, 101];

/** OCC-6 — turni che costituiscono capacità impiegabile (denominatore). */
export const CAPACITY_SHIFT_TYPES = ['available', 'extra', 'stationed', 'stationed_malpensa'];

/** OCC-6 — turni che rappresentano indisponibilità: contati, fuori dal denominatore. */
export const ABSENCE_SHIFT_TYPES = [
  'pause',
  'vacation',
  'sick',
  'unavailable',
  'permission',
  'assenza_ingiustificata',
];

const SECONDS_PER_MINUTE = 60;
const MINUTE_MS = 60 * 1000;

/**
 * OCC-1 — converte una durata legacy in minuti, gestendo il doppio formato
 * storico: secondi (valori > 180, da API Maps/pricing) e minuti (≤ 180, da
 * inserimento manuale o dati legacy).
 * Identica a `tempoToMinutes` in timelineDataHandlers.js.
 */
export function toMinutes(value, fallback = DEFAULT_DURATION_MIN) {
  if (!value) return fallback;
  return value > SECONDS_THRESHOLD ? value / SECONDS_PER_MINUTE : value;
}

/**
 * OCC-2/3/4/5 — scomposizione della durata pianificata di un ordine.
 *
 * `order` è già normalizzato dal chiamante:
 *   { tempo, fermo_auto, tempo_ritorno, secondo_operatore, secondo_operatore_arrivo }
 *
 * - OCC-2 `durationMin`: durata di marcia (`tempo`, formato duale, default 45).
 * - OCC-3 `fermoAutoMin`: minuti di fermo mezzo, così come sono (nessun default).
 * - OCC-4 `tempoRitornoMin`: conteggiato **solo se `fermo_auto > 0`**; in quel
 *   caso `tempo_ritorno` in formato duale con fallback = `durationMin`.
 * - OCC-5 `secondOperatorMin`: un'assistenza (`SECOND_OPERATOR_MIN`) per
 *   ogni luogo con un secondo operatore — `secondo_operatore` alla partenza,
 *   `secondo_operatore_arrivo` all'arrivo — e con il fermo auto il doppio:
 *   ogni luogo si visita due volte (andata e rientro).
 */
export function orderDurationParts(order) {
  const durationMin = toMinutes(order?.tempo, DEFAULT_DURATION_MIN);
  const fermoAutoMin = Number(order?.fermo_auto) || 0;
  const tempoRitornoMin = fermoAutoMin > 0 ? toMinutes(order?.tempo_ritorno, durationMin) : 0;

  const places = (order?.secondo_operatore ? 1 : 0) + (order?.secondo_operatore_arrivo ? 1 : 0);
  const secondOperatorMin = places * SECOND_OPERATOR_MIN * (fermoAutoMin > 0 ? 2 : 1);

  return {
    durationMin,
    fermoAutoMin,
    tempoRitornoMin,
    secondOperatorMin,
    // OCC-2..4 — `totalTime` della Timeline: NON include lo slot del secondo
    // operatore, che allunga solo il box renderizzato.
    totalMin: durationMin + fermoAutoMin + tempoRitornoMin,
    // Durata pianificata dell'occupazione, secondo operatore incluso.
    plannedSpanMin: durationMin + fermoAutoMin + tempoRitornoMin + secondOperatorMin,
  };
}

/**
 * OCC-7 — fine reale di un ordine concluso e ritardo in arrivo.
 * Il box non si accorcia mai sotto la fine pianificata: un anticipo mantiene la
 * lunghezza pianificata (il renderer ci disegna dentro l'anticipo), un ritardo
 * la allunga. `delayMin` è con segno; `null` se non c'è un tempo reale usabile.
 * Identica a `resolveRealEnd` in timelineDataHandlers.js.
 */
export function resolveRealEndMs(order, startMs, plannedEndMs) {
  const arrivalMs = order?.actual_arrival_ms;
  if (!COMPLETED_ORDER_STATUSES.includes(order?.status) || !arrivalMs) {
    return { endMs: plannedEndMs, plannedEndMs, delayMin: null };
  }
  if (!Number.isFinite(arrivalMs) || arrivalMs <= startMs) {
    return { endMs: plannedEndMs, plannedEndMs, delayMin: null };
  }
  return {
    endMs: Math.max(arrivalMs, plannedEndMs),
    plannedEndMs,
    delayMin: Math.round((arrivalMs - plannedEndMs) / MINUTE_MS),
  };
}

/**
 * OCC-7 — stati che nel nodo `history` marcano l'inizio effettivo della corsa
 * (7 "A bordo", 4 "Vicino Partenza") e la sua conclusione (10 "Completato da
 * APP", 5 "Completato"). L'ordine conta: `10` vince su `5`.
 */
export const HISTORY_DEPARTURE_STATUSES = [7, 4];
export const HISTORY_ARRIVAL_STATUSES = [10, 5];

/**
 * OCC-7 — tempi reali derivati dal nodo `history` di un ordine, quando i campi
 * `actual_*` non ci sono.
 *
 * Serve perché i campi `actual_*` li scrive **solo** il job di progressione GPS
 * (`checkingOrders/listeners/OrdersProgress.js`): un ordine concluso dall'app
 * autista o chiuso a mano cambia stato senza scriverli, e il suo tempo reale
 * esiste solo come voce di storia. Senza questa derivazione la Timeline mostra
 * il ritardo e le analytics dicono "dati insufficienti" sullo stesso ordine.
 *
 * Il nodo `history` ha DUE forme, entrambe da gestire, con perfino il campo
 * temporale diverso:
 *   (a) `{ date, status }`                          ← OrdersDAO.setStatus
 *   (b) `{ ts, changes: { status: { from, to } } }`  ← ObjectFirestoreDao
 *
 * Restituisce i valori **grezzi** (Timestamp Firestore, Date o stringa): la
 * conversione spetta al chiamante, che sa in che forma gli servono.
 *
 * @returns {{departure: *|null, arrival: *|null}}
 */
export function findActualTimesInHistory(history) {
  if (!Array.isArray(history) || history.length === 0) return { departure: null, arrival: null };

  /** Prima voce che porta lo stato cercato, in una delle due forme. */
  const firstWithStatus = (status) => {
    for (const entry of history) {
      if (!entry) continue;
      if (entry.status !== undefined) {
        if (Number(entry.status) === status && entry.date) return entry.date;
        continue;
      }
      const change = entry.changes && entry.changes.status;
      if (change && Number(change.to) === status && entry.ts) return entry.ts;
    }
    return null;
  };

  /** Primo stato della lista che compare nella storia: la priorità è la lista. */
  const firstOf = (statuses) => {
    for (const status of statuses) {
      const value = firstWithStatus(status);
      if (value) return value;
    }
    return null;
  };

  return {
    departure: firstOf(HISTORY_DEPARTURE_STATUSES),
    arrival: firstOf(HISTORY_ARRIVAL_STATUSES),
  };
}

/**
 * OCC-7 — ritardo in partenza con segno (positivo = partito tardi), `null` se
 * l'app non ha mai timbrato una partenza. Puramente informativo: non sposta
 * l'inizio del segmento. Identica a `resolveDepartDelay`.
 */
export function resolveDepartDelayMin(order, plannedStartMs) {
  const departureMs = order?.actual_departure_ms;
  if (!departureMs || !Number.isFinite(departureMs)) return null;
  return Math.round((departureMs - plannedStartMs) / MINUTE_MS);
}

/**
 * OCC-9 — un ordine occupa l'autista? Solo gli annullati non occupano: il loro
 * tempo si libera (resta contabilizzato a parte come capacità sprecata).
 */
export function orderOccupiesDriver(order) {
  return !CANCELLED_ORDER_STATUSES.includes(order?.status);
}

/**
 * OCC-9 — segmenti del SECONDO operatore, sulla sua riga autista.
 * Nella Timeline sono item clone generati da `duplicate2op`; lato backend gli
 * equivalenti sono le colonne `secondary_driver_id` (partenza) e
 * `secondary_driver_id_arrival` (arrivo) sulla stessa riga ordine.
 *
 * I due campi sono LUOGHI. La corsa è: [assistenza partenza] marcia
 * [assistenza arrivo], e con il fermo auto poi fermo [assistenza arrivo]
 * rientro [assistenza partenza]. Quindi:
 *  - operatori diversi (o uno solo): un segmento `SECOND_OPERATOR_MIN` per ogni
 *    assistenza del suo luogo, all'ora in cui cade;
 *  - stesso operatore sui due luoghi: a bordo per la tratta — l'andata e, con
 *    il fermo auto, il rientro; durante il fermo è libero.
 * Stessa forma di `tripLayout` in `secondOperatorSchedule.js`.
 */
export function buildSecondOperatorSegments(order) {
  const parts = orderDurationParts(order);
  const startMs = order?.start_ms;
  const pickup = order?.secondo_operatore || null;
  const arrival = order?.secondo_operatore_arrivo || null;
  const waits = parts.fermoAutoMin > 0;
  const A = SECOND_OPERATOR_MIN;
  const F = pickup ? A : 0;
  const T = arrival ? A : 0;
  // Minuto in cui comincia ogni assistenza, dall'inizio della corsa.
  const atDropOff = F + parts.durationMin;
  const atReturnPickup = atDropOff + T + parts.fermoAutoMin;
  const atReturnDropOff = atReturnPickup + (waits ? T : 0) + parts.tempoRitornoMin;

  const make = (driverId, fromMin, minutes) => ({
    driver_id: driverId,
    kind: 'second_operator',
    capacity: false,
    start_ms: startMs + fromMin * MINUTE_MS,
    end_ms: startMs + (fromMin + minutes) * MINUTE_MS,
    meta: { order_id: order?.id, status: order?.status, source: 'second_operator' },
  });

  if (pickup && arrival && pickup === arrival) {
    const legs = [make(pickup, 0, F + parts.durationMin + T)];
    if (waits) legs.push(make(pickup, atReturnPickup, T + parts.tempoRitornoMin + F));
    return legs;
  }
  const segments = [];
  if (pickup) segments.push(make(pickup, 0, A));
  if (arrival) segments.push(make(arrival, atDropOff, A));
  if (waits && arrival) segments.push(make(arrival, atReturnPickup, A));
  if (waits && pickup) segments.push(make(pickup, atReturnDropOff, A));
  return segments;
}

/**
 * Segmenti di occupazione di un ordine, in ordine cronologico.
 * Un ordine ne produce diversi, così la composizione del tooltip esce gratis e
 * il tempo a vuoto resta separabile dal servizio.
 *
 *   deadhead   [start − deadhead_min , start)     ← solo se noto (vedi OCC-10)
 *   second_op  [start , +assistenza partenza)      ← se c'è `secondo_operatore`
 *   job        [+durationMin)
 *   second_op  [+assistenza arrivo)                ← se c'è `secondo_operatore_arrivo`
 *   downtime   [+fermoAutoMin)
 *   second_op  [+assistenza arrivo)                ← al rientro, col fermo auto
 *   return     [+tempoRitornoMin)
 *   second_op  [+assistenza partenza)              ← al rientro, col fermo auto
 *   delay      [+ritardo]                          ← se l'arrivo reale sfora
 *
 * @param {object} order normalizzato, con `start_ms` e i campi durata.
 * @returns {{segments: Array, delayMin: number|null, departDelayMin: number|null,
 *            plannedEndMs: number, endMs: number}}
 */
export function buildOrderSegments(order) {
  const driverId = order?.driver_id;
  const startMs = order?.start_ms;
  const parts = orderDurationParts(order);

  const plannedEndMs = startMs + parts.plannedSpanMin * MINUTE_MS;
  const { endMs, delayMin } = resolveRealEndMs(order, startMs, plannedEndMs);
  const departDelayMin = resolveDepartDelayMin(order, startMs);

  const base = {
    driver_id: driverId,
    capacity: false,
    meta: {
      order_id: order?.id,
      group_id: order?.group_id || null,
      service_mode: order?.service_mode || null,
      status: order?.status,
      distance_km: Number(order?.distance) || 0,
      delay_departure_min: departDelayMin,
      delay_arrival_min: delayMin,
      source: 'order',
    },
  };

  const segments = [];
  const push = (kind, fromMs, minutes) => {
    if (!(minutes > 0)) return fromMs;
    const toMs = fromMs + minutes * MINUTE_MS;
    segments.push({ ...base, kind, start_ms: fromMs, end_ms: toMs });
    return toMs;
  };

  // OCC-10 — il tempo a vuoto precede la partenza. Disponibile solo dove il
  // dato è specchiato (vedi contratto): quando manca non si stima.
  const deadheadMin = Number(order?.deadhead_min) || 0;
  if (deadheadMin > 0) {
    segments.push({
      ...base,
      kind: 'deadhead',
      start_ms: startMs - deadheadMin * MINUTE_MS,
      end_ms: startMs,
    });
  }

  const F = order?.secondo_operatore ? SECOND_OPERATOR_MIN : 0;
  const T = order?.secondo_operatore_arrivo ? SECOND_OPERATOR_MIN : 0;
  const waits = parts.fermoAutoMin > 0;
  let cursor = startMs;
  cursor = push('second_operator', cursor, F);
  cursor = push('job', cursor, parts.durationMin);
  cursor = push('second_operator', cursor, T);
  cursor = push('downtime', cursor, parts.fermoAutoMin);
  cursor = push('second_operator', cursor, waits ? T : 0);
  cursor = push('return', cursor, parts.tempoRitornoMin);
  cursor = push('second_operator', cursor, waits ? F : 0);

  // OCC-7 — la coda di ritardo è occupazione reale: estende l'ultimo segmento
  // fino alla fine effettiva. Non è una metrica a sé (non va risommata).
  if (endMs > cursor && segments.length > 0) {
    segments[segments.length - 1].end_ms = endMs;
  }

  return { segments, delayMin, departDelayMin, plannedEndMs, endMs };
}

/**
 * OCC-16 — trasferimento a vuoto della PRIMA corsa del giorno: l'autista parte
 * dalla base e non c'è un servizio precedente da cui misurare. Stessa costante
 * della Timeline (`enrichItemsWithArrivalTime`).
 */
export const FIRST_RIDE_DEADHEAD_MIN = 20;

/**
 * OCC-16 — piano di calcolo del tempo a vuoto per la giornata di UN autista.
 *
 * Non calcola nulla: dice *cosa* va calcolato, così il routing (che costa
 * chiamate esterne) resta fuori dalla logica e questa resta pura e testabile.
 *
 * @param {Array} services servizi del giorno, ognuno
 *   `{ id, start_ms, end_ms, from_address, to_address }`
 * @returns {Array<{order_id, minutes?:number, from?:string, to?:string}>}
 *   voci con `minutes` sono già risolte (prima corsa); quelle con `from`/`to`
 *   richiedono un instradamento.
 */
export function buildDeadheadPlan(services) {
  const sorted = (services || [])
    .filter((s) => s && Number.isFinite(s.start_ms))
    .slice()
    .sort((a, b) => a.start_ms - b.start_ms);

  return sorted.map((service, index) => {
    if (index === 0) {
      // Prima corsa: convenzione fissa, non c'è un "da dove" misurabile.
      return { order_id: service.id, minutes: FIRST_RIDE_DEADHEAD_MIN };
    }
    const previous = sorted[index - 1];
    // Senza indirizzi non si instrada: meglio nessun dato che una stima muta.
    if (!previous.to_address || !service.from_address) {
      return { order_id: service.id, minutes: null };
    }
    return {
      order_id: service.id,
      from: previous.to_address,
      to: service.from_address,
    };
  });
}

/**
 * OCC-16 — il trasferimento non può durare più del buco che lo contiene.
 * L'instradamento stima il tempo in condizioni ideali; se il servizio
 * precedente è finito 10 minuti prima, il trasferimento reale è al massimo 10.
 * Serve anche a non generare sovrapposizioni artificiali col servizio prima.
 */
export function clampDeadheadToGap(routedMin, previousEndMs, startMs) {
  if (routedMin == null) return null;
  const gapMin = (startMs - previousEndMs) / MINUTE_MS;
  if (!(gapMin > 0)) return 0;
  return Math.max(0, Math.min(routedMin, gapMin));
}

/** OCC-14 — durata di default di un evento senza `duration` (minuti). */
export const DEFAULT_EVENT_DURATION_MIN = 30;

/**
 * OCC-14 — segmento di un evento (pulizia, ricarica, manutenzione, corso,
 * viaggio a vuoto, ...). Occupa l'autista come un servizio, ma non ha service
 * mode né scomposizione: è un blocco unico.
 *
 * `duration` è in MINUTI (non nel doppio formato degli ordini) e vale 30 se
 * assente — stessa regola dell'handler `event` della Timeline.
 * Restituisce `null` se l'evento non è collocabile.
 */
export function buildEventSegment(event) {
  const startMs = event?.start_ms;
  const driverId = event?.driver_id;
  if (!driverId || !Number.isFinite(startMs)) return null;

  const minutes = Number(event?.duration) > 0 ? Number(event.duration) : DEFAULT_EVENT_DURATION_MIN;

  return {
    driver_id: driverId,
    kind: 'event',
    capacity: false,
    start_ms: startMs,
    end_ms: startMs + minutes * MINUTE_MS,
    meta: {
      event_id: event?.id,
      event_type: event?.type || null,
      vehicle_id: event?.vehicle_id || null,
      distance_km: Number(event?.distance) || 0,
      source: 'event',
    },
  };
}

/**
 * OCC-13 — sosta a ogni fermata di un giro in pooling, in secondi. Stessi
 * valori di `DWELL_SECONDS` in `src/modules/logic/groupRoute.js` (qui niente
 * import): base a ogni fermata, più il tempo carrozzina per ogni carrozzina.
 */
export const GROUP_STOP_DWELL_SECONDS = 120;
export const GROUP_WHEELCHAIR_DWELL_SECONDS = 300;

/**
 * OCC-13 — fermate di un giro: quelle del percorso applicato (`stops` sul
 * `trip_group`, già accorpate per luogo) se ci sono; altrimenti una presa per
 * leg più la destinazione comune.
 */
export function groupStopCount(group) {
  const routed = Array.isArray(group?.stops) ? group.stops.length : 0;
  if (routed > 0) return routed;
  return (Number(group?.legs) || 1) + 1;
}

/**
 * OCC-13 — minuti di sosta di un giro, come li calcola il percorso
 * (`scheduleForward`): sosta base a ogni fermata + tempo carrozzina.
 */
export function groupOverheadMin(group) {
  const wheelchairs = Number(group?.wheelchair_count) || 0;
  const seconds =
    groupStopCount(group) * GROUP_STOP_DWELL_SECONDS + wheelchairs * GROUP_WHEELCHAIR_DWELL_SECONDS;
  return seconds / 60;
}

/**
 * OCC-13 — durata di un giro in POOLING, che occupa l'autista **una volta
 * sola** anche se raggruppa più ordini, e **provenienza** del numero.
 *
 * Tre rami, in ordine di precisione decrescente. `source` va sempre esposto:
 * un giro misurato e un giro stimato non sono lo stesso dato, e la UI deve
 * poter dire quale dei due sta mostrando.
 *
 *  1. `routed` — `pooling_duration`: secondi di guida **reale** del giro
 *     completo (Maps, instradamento per tutte le tappe, su `trip_groups`), più
 *     l'overhead di salita/discesa. È l'unico ramo che conosce il percorso
 *     effettivamente fatto.
 *  2. `legs_span` — dagli **orari di presa** dei leg: dalla prima presa
 *     all'ultima discesa pianificata (`legs_span_min`, vedi
 *     `collapseGroupLegs`), più l'overhead. Le prese scaglionate sono la
 *     traccia che il giro lascia sui dati anche senza instradamento: un giro
 *     che prende alle 09:00, 09:10 e 09:20 dura almeno fino alla discesa
 *     dell'ultimo, non il tempo di una corsa sola.
 *  3. `representative` — tempo della corsa rappresentativa. Vale solo per un
 *     giro di un leg solo o con orari inutilizzabili: **sottostima** il giro
 *     (ignora sia le tappe sia il percorso) ed è il ripiego, non la regola.
 *
 * Il ramo 2 non può risultare più corto del ramo 3: un giro non dura meno
 * della singola corsa che lo rappresenta.
 *
 * Note invariate: per un gruppo NON si contano né il tempo di ritorno né gli
 * slot del secondo operatore (la Timeline non li applica al box di gruppo), e
 * il fermo mezzo entra solo nei rami 2 e 3 — `pooling_duration` è già il tempo
 * del giro porta a porta.
 *
 * @returns {{rideMin:number, fermoAutoMin:number, overheadMin:number,
 *            totalMin:number, source:'routed'|'legs_span'|'representative'}}
 */
export function groupDurationParts(group) {
  const overheadMin = groupOverheadMin(group);
  const fermoAutoMin = Number(group?.fermo_auto) || 0;

  const poolingSec = Number(group?.pooling_duration) || 0;
  if (poolingSec > 0) {
    const rideMin = poolingSec / SECONDS_PER_MINUTE + overheadMin;
    return { rideMin, fermoAutoMin: 0, overheadMin, totalMin: rideMin, source: 'routed' };
  }

  const representativeMin = toMinutes(group?.tempo);
  const spanMin = Number(group?.legs_span_min);
  if (Number.isFinite(spanMin) && spanMin > 0) {
    const rideMin = Math.max(spanMin + overheadMin, representativeMin);
    return {
      rideMin,
      fermoAutoMin,
      overheadMin,
      totalMin: rideMin + fermoAutoMin,
      source: 'legs_span',
    };
  }

  return {
    rideMin: representativeMin,
    fermoAutoMin,
    overheadMin,
    totalMin: representativeMin + fermoAutoMin,
    source: 'representative',
  };
}

/** OCC-13 — durata totale di un giro in pooling (minuti). */
export function groupDurationMin(group) {
  return groupDurationParts(group).totalMin;
}

/**
 * OCC-13 — collassa i leg di un giro in pooling in **un solo** ordine di
 * gruppo, con i dati aggregati che servono a misurarlo.
 *
 * È il passaggio che chi legge le righe grezze (le analytics, dal mirror CRM)
 * deve fare prima di generare segmenti: senza, un pooling da 3 passeggeri
 * emette 3 serie di segmenti, `trips` e km triplicano e nascono
 * sovrapposizioni fantasma che segnalerebbero una doppia prenotazione
 * inesistente.
 *
 * Regole di aggregazione:
 *  - **rappresentante** = il leg che parte per primo (non il primo in lista:
 *    l'ordinamento della query non è una garanzia);
 *  - **`legs_span_min`** = dalla prima presa all'ultima discesa pianificata,
 *    `max(presa_leg + marcia_leg) − min(presa_leg)`. Il fermo mezzo resta
 *    fuori (lo aggiunge `groupDurationParts` una volta sola, sommato sui leg):
 *    dentro lo span verrebbe contato due volte;
 *  - **km** = somma dei leg, dichiarata come limite **superiore** (i tratti
 *    condivisi del giro finiscono contati più volte). Resta la convenzione
 *    dell'app — `formatOrders` somma le distanze allo stesso modo — ed è
 *    comunque preferibile al solo leg rappresentativo, che è un limite
 *    inferiore molto più lontano dal vero;
 *  - **tempi reali (OCC-7)**: partenza = quella del **primo** leg e solo quella
 *    (un timbro su una presa successiva non è la partenza del giro); arrivo =
 *    l'**ultima** timbrata fra i leg **conclusi**. È la misura più precisa della
 *    fine del giro e generalizza la cascata della Timeline
 *    (`resolveGroupRealEnd`: ultimo leg, poi penultimo quando l'app lascia
 *    l'ultimo appeso) — l'app lascia appesi anche leg intermedi, e "l'ultimo
 *    concluso" copre tutti quei casi con una regola sola;
 *  - **annullati (OCC-8)**: i leg annullati non allungano il giro e non
 *    portano km. Se sono annullati **tutti**, il gruppo collassato resta con lo
 *    stato annullato del primo leg, così il chiamante lo conta una volta sola
 *    come capacità sprecata.
 *
 * @param {Array} legs leg normalizzati (stessa forma di `buildOrderSegments`).
 * @returns {object|null} ordine di gruppo, o `null` se nessun leg è collocabile.
 */
export function collapseGroupLegs(legs) {
  const usable = (legs || []).filter((leg) => leg && Number.isFinite(leg.start_ms));
  if (usable.length === 0) return null;

  const sorted = usable.slice().sort((a, b) => a.start_ms - b.start_ms);
  const active = sorted.filter(orderOccupiesDriver);
  // Tutti annullati: nessuna corsa da misurare, ma il gruppo va restituito
  // (con lo stato annullato) perché il chiamante lo contabilizzi come persa.
  const counted = active.length > 0 ? active : sorted;

  const startMs = counted[0].start_ms;
  let plannedEndMs = startMs;
  let distance = 0;
  let fermoAutoMin = 0;
  let arrivalMs = null;

  for (const leg of counted) {
    // Marcia del leg: il fermo mezzo NON entra nello span (vedi sopra).
    const legEndMs = leg.start_ms + toMinutes(leg.tempo) * MINUTE_MS;
    if (legEndMs > plannedEndMs) plannedEndMs = legEndMs;

    distance += Number(leg.distance) || 0;
    fermoAutoMin += Number(leg.fermo_auto) || 0;

    const arrival = leg.actual_arrival_ms;
    if (
      COMPLETED_ORDER_STATUSES.includes(leg.status) &&
      Number.isFinite(arrival) &&
      arrival &&
      (arrivalMs === null || arrival > arrivalMs)
    ) {
      arrivalMs = arrival;
    }
  }

  const representative = counted[0];

  // OCC-7 — la partenza del giro è quella del PRIMO leg, e solo quella: uno
  // stampo su una presa successiva non dice quando il giro è partito (l'app
  // timbra la salita di quel passeggero, che è già in mezzo al giro). Senza
  // quel timbro il ritardo in partenza resta ignoto — meglio `null` che un
  // ritardo inventato dal timbro di una tappa intermedia. Stessa scelta di
  // `resolveGroupDepartDelay` nella Timeline, che guarda solo `firstLegOrder`.
  const firstDeparture = representative.actual_departure_ms;
  const departureMs = Number.isFinite(firstDeparture) && firstDeparture ? firstDeparture : null;

  return {
    ...representative,
    id: representative.id,
    group_id: representative.group_id,
    start_ms: startMs,
    // Un leg solo: nessuno span da misurare, si resta sul ramo `representative`.
    legs_span_min: counted.length > 1 ? (plannedEndMs - startMs) / MINUTE_MS : null,
    legs: counted.length,
    leg_ids: counted.map((leg) => leg.id),
    fermo_auto: fermoAutoMin,
    distance,
    distance_source: 'legs_sum',
    actual_departure_ms: departureMs,
    // OCC-7 — arrivo già filtrato ai soli leg conclusi: lo consuma
    // `resolveGroupRealEndMs`, che non richiede lo stato del gruppo (il gruppo
    // non ha un documento proprio, quindi non ha uno stato "concluso" suo).
    actual_arrival_ms: arrivalMs,
    // Un giro non ha né ritorno né secondo operatore (OCC-13).
    tempo_ritorno: null,
    secondo_operatore: null,
    secondo_operatore_arrivo: null,
  };
}

/**
 * OCC-7/OCC-13 — fine reale di un GIRO in pooling. Come `resolveRealEndMs`, ma
 * senza il controllo sullo stato: un gruppo non ha un documento proprio e
 * quindi non ha uno stato "concluso" suo. L'arrivo è già stato filtrato ai soli
 * leg conclusi da `collapseGroupLegs` (equivalente del `resolveGroupRealEnd`
 * della Timeline, che pesca l'arrivo dall'ultimo leg).
 */
export function resolveGroupRealEndMs(group, startMs, plannedEndMs) {
  const arrivalMs = group?.actual_arrival_ms;
  if (!arrivalMs || !Number.isFinite(arrivalMs) || arrivalMs <= startMs) {
    return { endMs: plannedEndMs, plannedEndMs, delayMin: null };
  }
  return {
    endMs: Math.max(arrivalMs, plannedEndMs),
    plannedEndMs,
    delayMin: Math.round((arrivalMs - plannedEndMs) / MINUTE_MS),
  };
}

/**
 * OCC-13 — segmenti di un giro in pooling. Un solo blocco `pooling`: la
 * scomposizione servizio/fermo/ritorno non si applica a un giro raggruppato.
 * `duration_source` / `distance_source` viaggiano nei meta: il drill-down deve
 * poter distinguere un giro instradato da uno stimato dagli orari di presa.
 */
export function buildGroupSegments(group) {
  const startMs = group?.start_ms;
  const parts = groupDurationParts(group);
  const plannedEndMs = startMs + parts.totalMin * MINUTE_MS;
  const { endMs, delayMin } = resolveGroupRealEndMs(group, startMs, plannedEndMs);
  const departDelayMin = resolveDepartDelayMin(group, startMs);

  return {
    segments: [
      {
        driver_id: group?.driver_id,
        kind: 'pooling',
        capacity: false,
        start_ms: startMs,
        end_ms: Math.max(endMs, plannedEndMs),
        meta: {
          order_id: group?.id,
          group_id: group?.group_id,
          service_mode: group?.service_mode || null,
          status: group?.status,
          distance_km: Number(group?.distance) || 0,
          distance_source: group?.distance_source || null,
          delay_departure_min: departDelayMin,
          delay_arrival_min: delayMin,
          legs: Number(group?.legs) || 1,
          duration_source: parts.source,
          ride_min: parts.rideMin,
          fermo_auto_min: parts.fermoAutoMin,
          source: 'group',
        },
      },
    ],
    delayMin,
    departDelayMin,
    plannedEndMs,
    endMs,
    durationSource: parts.source,
  };
}

/**
 * OCC-6 — segmento di capacità/assenza da un turno. `null` se il tipo non è
 * riconosciuto o l'intervallo non è valido.
 * `pauseAsCapacity` sposta le pause nel denominatore (toggle di UI).
 */
export function buildShiftSegment(shift, { pauseAsCapacity = false } = {}) {
  const type = shift?.type;
  const startMs = shift?.start_ms;
  const endMs = shift?.end_ms;
  if (!shift?.driver_id || !Number.isFinite(startMs) || !Number.isFinite(endMs)) return null;
  if (endMs <= startMs) return null;

  const isCapacity = CAPACITY_SHIFT_TYPES.includes(type) || (pauseAsCapacity && type === 'pause');
  const isAbsence = ABSENCE_SHIFT_TYPES.includes(type);
  if (!isCapacity && !isAbsence) return null;

  return {
    driver_id: shift.driver_id,
    kind: type,
    capacity: isCapacity,
    start_ms: startMs,
    end_ms: endMs,
    meta: { shift_id: shift.id, source: 'shift' },
  };
}

/**
 * Unione degli intervalli di una lista di segmenti (già filtrati per autista).
 * Serve sia per `busy_min` sia per `capacity_min`: senza unione due segmenti
 * sovrapposti gonfierebbero la saturazione oltre il reale.
 * @returns {Array<{start_ms:number,end_ms:number}>} intervalli disgiunti ordinati.
 */
export function mergeIntervals(segments) {
  const sorted = segments
    .filter((s) => Number.isFinite(s?.start_ms) && s.end_ms > s.start_ms)
    .map((s) => ({ start_ms: s.start_ms, end_ms: s.end_ms }))
    .sort((a, b) => a.start_ms - b.start_ms);

  const merged = [];
  for (const interval of sorted) {
    const last = merged[merged.length - 1];
    if (last && interval.start_ms <= last.end_ms) {
      if (interval.end_ms > last.end_ms) last.end_ms = interval.end_ms;
    } else {
      merged.push({ ...interval });
    }
  }
  return merged;
}

/** Minuti totali di una lista di intervalli disgiunti. */
export function totalMinutes(intervals) {
  return intervals.reduce((sum, i) => sum + (i.end_ms - i.start_ms) / MINUTE_MS, 0);
}
